/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Tooltip } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ModuleAccessSelector } from "../module_access_selector";
import { PermissionProfilePicker } from "../permission_profile_picker";
import { FieldLabel, tooltipIconStyle } from "./styles";

const MODULE_ACCESS_HINT =
  'Choose which parts of the Admin App this user can open. Their role above still decides what ' +
  'they can do inside those modules.';

/**
 * Module access block of the user modal: the permission profile picker and the module grid it
 * fills in.
 *
 * Kept in its own file and fed by a single `moduleAccess` object so the shape of this data can be
 * revised without touching the modal shell or the identity and role fields around it.
 *
 * @param {Object} moduleAccess everything the picker and the grid read, see `buildModuleAccess`
 *                              in the user management page for the shape
 * @param {String} testIdPrefix `create` or `edit`, so both modals stay separately addressable
 */
export const ModuleAccessSection = ({ moduleAccess, testIdPrefix }) => {
  if (!moduleAccess) return null;

  const {
    groups,
    moduleKeySet,
    grantableKeySet,
    unavailableModules,
    modulesLoading,
    modulesError,
    refetchModules,
    catalogueReady,
    profileOptions,
    profilesLoading,
    profilesError,
    refetchProfiles,
    selectedKeys,
    onSelectedKeysChange,
    profileId,
    selectedProfile,
    assignedProfile,
    isProfileNotApplicable,
    onProfileChange,
    summaryLabel,
    profileKeySet,
    hasRoles,
  } = moduleAccess;

  return (
    <React.Fragment>
      <FieldLabel as="div">
        Module Access
        <Tooltip title={MODULE_ACCESS_HINT} arrow placement="top">
          <InfoCircleOutlined style={tooltipIconStyle} />
        </Tooltip>
      </FieldLabel>

      <PermissionProfilePicker
        options={profileOptions}
        value={profileId}
        selectedProfile={selectedProfile}
        assignedProfile={assignedProfile}
        notApplicable={isProfileNotApplicable}
        onChange={onProfileChange}
        isLoading={profilesLoading}
        isError={profilesError}
        onRetry={refetchProfiles}
        disabled={!catalogueReady}
        hasRoles={hasRoles}
        testIdPrefix={`${testIdPrefix}-profile-picker`}
      />

      <ModuleAccessSelector
        groups={groups}
        selectedKeys={selectedKeys}
        moduleKeySet={moduleKeySet}
        selectableKeySet={grantableKeySet}
        unavailableModules={unavailableModules}
        summaryLabel={summaryLabel}
        profileKeySet={profileKeySet}
        onChange={onSelectedKeysChange}
        isLoading={modulesLoading}
        isError={modulesError}
        onRetry={refetchModules}
        testIdPrefix={`${testIdPrefix}-module-access`}
      />
    </React.Fragment>
  );
};

export default ModuleAccessSection;
